import React, { useCallback, useContext, useState } from 'react'
import { Form, Checkbox } from 'semantic-ui-react'

import * as R from 'ramda'

import { QuestionBuilderContext } from 'organisms/QuestionBuilderCard'
import { TooltipLabel } from 'components/TooltipLabel'
import ChoiceQuestionInput from 'components/ChoiceQuestionInput'
import { RatingScaleButtonGroup } from 'components/RatingScaleButtonGroup'
import SentimentChoiceInput from 'organisms/SentimentChoiceInput'
import { hasSentimentRule, sortChoiceValue } from 'organisms/SurveySettingsV2/utils'

const SCALE_RATING = {
  2: [4, 2],
  3: [4, 3, 2],
  4: [5, 4, 3, 2],
  5: [5, 4, 3, 2, 1],
}

// The opinion-scale UI maps at most 5 choices to rating slots. A multiple-choice
// question can now carry up to 10 options, so the choices that don't fit the
// current scale are held aside (in non-opinion form) and merged back when the
// scale is raised or opinion-scale is toggled off — so toggling never loses work.
// These overflow choices are NOT persisted while opinion-scale is on (the save
// path soft-deletes them); persisting them would leak non-rating options into the
// respondent survey and break sentiment reporting.
export const OPINION_SCALE_MAX_VISIBLE = 5

export function splitChoicesForOpinionScale(choices, scale = OPINION_SCALE_MAX_VISIBLE) {
  return {
    opinionChoices: choices.slice(0, scale),
    overflowChoices: choices.slice(scale),
  }
}

export function mergeChoicesFromOpinionScale(activeOpinionChoices, overflowChoices) {
  return [...activeOpinionChoices, ...overflowChoices]
}

export function MultipleChoiceQuestionInput({ arrayHelpers, handleFieldChange }) {
  const { question } = useContext(QuestionBuilderContext)
  const [selectedScale, setSelectedScale] = useState(
    Math.min(question.choices.length, OPINION_SCALE_MAX_VISIBLE)
  )

  const preservedOverflowChoices = R.propOr([], 'preservedOpinionOverflowChoices', question)

  function _getActiveChoices(ratings, choices) {
    return choices.filter((choice) => {
      const choiceValue = typeof choice.value == 'string' ? Number(choice.value) : choice.value
      return ratings.includes(choiceValue)
    })
  }

  function _mapChoicesToNonOpinionScale(choices) {
    // Restore the non-opinion invariant: value === label (the answer text).
    // `Answer.rating` returns choice.value and the integration import matches
    // choices by value, so a non-opinion choice's value must be its label.
    return choices.map((choice) => {
      return { ...choice, sentiment: null, value: choice.label }
    })
  }

  function _mapChoicesToOpinionScale(choices, totalChoice) {
    function _getChoiceValue(choiceCount, index) {
      const choiceTotal = choiceCount > 5 ? 5 : choiceCount
      const mapValue = {
        2: [4, 2],
        3: [4, 3, 2],
        4: [5, 4, 3, 2],
        5: [5, 4, 3, 2, 1],
      }
      return R.path([choiceTotal, index], mapValue) || null
    }

    function _getSentiment(choiceValue) {
      if (choiceValue > 3) {
        return 'positive'
      } else if (choiceValue === 3) {
        return 'neutral'
      } else if (choiceValue < 3) {
        return 'negative'
      } else {
        return null
      }
    }

    return choices.map((choice, index) => {
      const value = _getChoiceValue(totalChoice, index)
      const sentiment = _getSentiment(value)
      return { ...choice, value, sentiment }
    })
  }

  // Turning opinion-scale ON: assign rating values to the first `scale` choices and
  // hold any beyond the scale aside. Only used on the initial toggle, where the
  // choices arrive in non-opinion form and need a value mapping.
  function _enterOpinionScale(choices, scale) {
    const { opinionChoices, overflowChoices } = splitChoicesForOpinionScale(choices, scale)
    handleFieldChange('choices', () => _mapChoicesToOpinionScale(opinionChoices, scale))()
    handleFieldChange('preservedOpinionOverflowChoices', () => overflowChoices)()
    handleFieldChange('ratingScale', () => scale)()
    setSelectedScale(scale)
  }

  function onScaleChange(scale) {
    // Keep each surviving choice's existing rating value — lowering the scale hides
    // the choices whose value is outside the new rating set rather than renumbering
    // the survivors. Renumbering would momentarily give two active choices the same
    // value during the row-by-row save and trip the unique constraint, and it isn't
    // the correct opinion-scale behaviour. Raising the scale brings the hidden
    // choices back by the same value-membership test.
    const ratings = SCALE_RATING[scale]
    const allChoices = mergeChoicesFromOpinionScale(question.choices, preservedOverflowChoices)
    const activeChoices = _getActiveChoices(ratings, allChoices)
    const hiddenChoices = allChoices.filter((choice) => !activeChoices.includes(choice))

    handleFieldChange('choices', () => activeChoices)()
    handleFieldChange('preservedOpinionOverflowChoices', () => hiddenChoices)()
    handleFieldChange('ratingScale', () => scale)()
    setSelectedScale(scale)
  }

  function onOpinionScaleChange(newStatus) {
    const isUnChecked = newStatus == false

    if (isUnChecked) {
      const ratings = SCALE_RATING[selectedScale]
      const activeChoices = _getActiveChoices(ratings, question.choices)
      // Restore every choice to non-opinion form (value === label). Preserved
      // choices may be held in opinion form (rating values, from a scale-down) or
      // non-opinion form (overflow beyond 5 from the initial toggle) — mapping both
      // guarantees value === label so reporting/integrations stay correct.
      const restoredActive = _mapChoicesToNonOpinionScale(sortChoiceValue(activeChoices))
      const restoredPreserved = _mapChoicesToNonOpinionScale(preservedOverflowChoices)
      const choices = mergeChoicesFromOpinionScale(restoredActive, restoredPreserved)

      handleFieldChange('choices', () => choices)()
      handleFieldChange('preservedOpinionOverflowChoices', () => [])()
    } else {
      const scale = Math.min(question.choices.length, OPINION_SCALE_MAX_VISIBLE)
      _enterOpinionScale(question.choices, scale)
    }

    handleFieldChange('isOpinionScale', () => newStatus)()

    const questionConditions = R.path(['questionCondition', 'conditions'], question) || []
    handleFieldChange(
      `shouldWarningResetSentimentRule`,
      () => hasSentimentRule(questionConditions) && isUnChecked
    )()
  }

  const onChoiceLabelChange = useCallback(
    (choice) => {
      const index = R.findIndex(R.propEq('value', choice.value), question.choices)
      const isUpdateChoice = index > -1
      if (isUpdateChoice) {
        handleFieldChange(`choices.${index}`, () => choice)()
      } else {
        handleFieldChange(`choices`, () => [...question.choices, choice])()
      }
    },
    [handleFieldChange, question.choices]
  )

  return (
    <React.Fragment>
      <Form.Field>
        <Checkbox
          label="Map choices to an opinion scale"
          checked={question.isOpinionScale}
          onChange={() => onOpinionScaleChange(!question.isOpinionScale)}
          data-testid="opinion-scale-toggle"
        />
        <span>
          <TooltipLabel
            descriptionTitle="Opinion scales"
            descriptionContent="When an opinion scale is activated on a multiple choice question, answers are mapped to either a negative, neutral, or positive sentiment."
            learnMoreLink="https://help.simplesat.io/en/articles/2776705-survey-questions#choices-and-opinion-scales"
          />
        </span>
      </Form.Field>
      {question.isOpinionScale ? (
        <React.Fragment>
          <Form.Field>
            <label>Rating scale</label>
            <RatingScaleButtonGroup scale={selectedScale} onChange={onScaleChange} />
          </Form.Field>
          <Form.Field>
            <label>Choices</label>
            <SentimentChoiceInput
              choices={question.choices.slice(0, OPINION_SCALE_MAX_VISIBLE)}
              scale={selectedScale}
              onChange={onChoiceLabelChange}
            />
          </Form.Field>
        </React.Fragment>
      ) : (
        <ChoiceQuestionInput
          choices={question.choices}
          deletedExistChoices={R.prop('deletedExistChoices', question) || []}
          arrayHelpers={arrayHelpers}
          handleFieldChange={handleFieldChange}
        />
      )}
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/MultipleChoiceQuestionInput/index.js